import React, { memo, useMemo } from 'react';
import { Trans } from 'react-i18next';

import { CheckoutContributionConfirmationResponseType } from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { UiMereLabel } from 'src/components/UI/UiMereLabel';
import { Enums } from 'src/models/enums';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { UiCard } from 'src/components/UI/Card';

type Props = {
  model: CheckoutContributionConfirmationResponseType['AdditionalInfoSubsection'];
};

export const CheckoutContributionAdditionalInfo = memo<Props>(({ model }) => {
  const locale = 'checkout.confirmation.additionalInfo';
  const tHelper = useTHelper(locale);

  const [recurrencePatternKey, recurrencePatternValues] = useMemo(() => {
    if (!model.RecurrencePattern) {
      return ['', {}];
    }

    const recurrenceType = model.RecurrencePattern.RecurrenceType;
    const { TotalOccurences, Ongoing, EndDate } = Enums.contributions.RecurrenceTypeCode;

    const params = {
      contributionSingular: model.ContributionTerminology.Singular?.toLowerCase() || '',
      totalAmount: model.TotalAmountString,
      periodSingular: model.RecurrencePattern.RecurrencePeriodSingular || '',
    };

    if (recurrenceType === TotalOccurences) {
      return [
        'recurrencePattern.totalOccurences',
        {
          ...params,
          totalOccurences: model.RecurrencePattern.TotalOccurences,
          periodPlural: model.RecurrencePattern.RecurrencePeriodPlural,
        },
      ];
    }

    if (recurrenceType === EndDate) {
      return ['recurrencePattern.endDate', { ...params, endDate: model.RecurrencePattern.EndDateString }];
    }

    if (recurrenceType === Ongoing) {
      return [
        'recurrencePattern.ongoing',
        {
          ...params,
          endDate: model.RecurrencePattern.EndDateString,
          projectSingular: model.IsDonationPage
            ? ''
            : tHelper('recurrencePattern.singular', {
                singular: model.ProjectTerminology.Singular?.toLowerCase() || '',
              }),
        },
      ];
    }

    return ['', {}];
  }, [
    model.ContributionTerminology.Singular,
    model.IsDonationPage,
    model.ProjectTerminology.Singular,
    model.RecurrencePattern,
    model.TotalAmountString,
    tHelper,
  ]);

  const link = useMemo(
    () => (
      <UiInnerNotSPALink className="link-hoverable" href={model.ManageContributionsUrl}>
        1
      </UiInnerNotSPALink>
    ),
    [model.ManageContributionsUrl]
  );

  return (
    <>
      <h2 className="section-header">{tHelper('title')}</h2>
      {model.RecurrencePattern && (
        <UiCard>
          <div className="row">
            <div className="col-sm-12 additional-info">
              <div className="col-sm-3">
                <UiMereLabel>
                  {`${model.RecurrencePattern.RecurrencePeriodLy} ${model.ContributionTerminology.Singular}`}
                </UiMereLabel>
              </div>
              <div className="col-sm-9 additional-info-body">
                <Trans i18nKey={`${locale}.${recurrencePatternKey}`} values={recurrencePatternValues}>
                  {link}
                </Trans>
              </div>
            </div>
          </div>
        </UiCard>
      )}
      {model.HasAnyPerUnitPledge && (
        <UiCard>
          <div className="row">
            <div className="col-sm-12 additional-info">
              <div className="col-sm-3">
                <UiMereLabel>{tHelper('pledges.label')}</UiMereLabel>
              </div>
              <div className="col-sm-9 additional-info-body ws-pw">
                <Trans
                  i18nKey={`${locale}.pledges.text`}
                  values={{
                    singular: model.TeamMemberTerminology.Singular?.toLowerCase() || '',
                    projectSingular: model.ProjectTerminology.Singular?.toLowerCase() || '',
                  }}
                >
                  {link}
                </Trans>
              </div>
            </div>
          </div>
        </UiCard>
      )}

      {model.HasBids && (
        <UiCard>
          <div className="row">
            <div className="col-sm-12 additional-info">
              <div className="col-sm-3">
                <UiMereLabel>{tHelper('bids.label')}</UiMereLabel>
              </div>
              <div className="col-sm-9 additional-info-body">
                <Trans
                  i18nKey={`${locale}.bids.charged`}
                  values={{ plural: model.AuctionItemsTerminology.Plural?.toLowerCase() || '' }}
                >
                  <br />
                </Trans>
                <div>
                  {tHelper(`bids.auctionSms${+model.AuctionSmsAlertsEnabled}`, {
                    singular: model.AuctionItemsTerminology.Singular?.toLowerCase() || '',
                  })}
                </div>
                <br />
                <Trans i18nKey={`${locale}.bids.increase`}>{link}</Trans>
              </div>
            </div>
          </div>
        </UiCard>
      )}

      {model.IsDedicatedDonation && (
        <UiCard>
          <div className="row">
            <div className="col-sm-12 additional-info">
              <div className="col-sm-3">
                <UiMereLabel>{tHelper('dedication.label')}</UiMereLabel>
              </div>
              <div className="col-sm-9 additional-info-body">
                <Trans
                  i18nKey={`${locale}.dedication.text`}
                  values={{
                    singular: model.ContributionTerminology.Singular?.toLowerCase() || '',
                    dedicationType: model.DedicationType,
                    dedicatedToName: model.DedicatedToName,
                  }}
                >
                  <span>1</span>
                </Trans>

                {model.DedicatedToEmail && (
                  <>
                    <br />
                    {tHelper('dedication.sent', { email: model.DedicatedToEmail })}
                  </>
                )}
              </div>
            </div>
          </div>
        </UiCard>
      )}

      {model.HasCustomFields && (
        <UiCard>
          {model.CustomFields.map((item) => (
            <div key={item.Name} className="row">
              <div className="col-sm-12 additional-info">
                <div className="col-sm-3">
                  <UiMereLabel>{item.Name}</UiMereLabel>
                </div>
                <div className="col-sm-9 additional-info-body">{item.Value}</div>
              </div>
            </div>
          ))}
        </UiCard>
      )}
    </>
  );
});
